import { useScreenTypeContext } from './state/screenTypeProvider'

import { recursiveFindValue } from '@eventr/common'
import {
    ColoursObj,
    Icon,
    TEventrIcons,
    TicketIcon,
} from '@eventr/common-storybook'
import { useShallow } from 'zustand/react/shallow'
import { useBoundStore } from '../state/bound.store'
import { ActivityCard } from './componentCards/activityCard'
import { FlightCard } from './componentCards/flightCard'
import { HireCarCard } from './componentCards/hireCarCard'
import { HotelCard } from './componentCards/hotelCard'
import { ParkingCard } from './componentCards/parkingCard'
import { TransportCard } from './componentCards/transportCard'
import { ArrayFinder } from './components/arrayFinder'
import { Container } from './components/container'
import { Counter } from './components/counter'
import { EditArrowIcon } from './components/editArrowIcon'
import { EditButton } from './components/editButton'
import { ExpandableCard } from './components/expandableCard'
import { FileList } from './components/fileList'
import { FlightPassengersBody } from './components/flightPassengersBody'
import { FromToTimes } from './components/fromToTimes'
import { HireCarsPassengersBody } from './components/hireCarsPassengersBody'
import { Iterator } from './components/iterator'
import { ListHeader } from './components/listHeader'
import { ParkingCarBody } from './components/parkingCarBody'
import { PushPathContainer } from './components/pushPathContainer'
import { RoomBody } from './components/roomBody'
import { ShouldRender } from './components/shouldRender'
import { SimpleCard } from './components/simpleCard'
import { SimpleFromToTimes } from './components/simpleFromToTimes'
import { SpaceBetween } from './components/spaceBetween'
import { Spacer } from './components/spacer'
import { SubItemEditButton } from './components/subPageEditButton'
import Text from './components/text'
import { TimeDuration } from './components/timeDuration'
import { TransportPassengersBody } from './components/transportPassengersBody'
import { UsersList } from './components/usersList'
import { useSectionV2Context } from './state/sectionProvider'

interface IComponentFinderProps {
    id?: string
    data?: any
    componentKey?: string
    renderOptions?: any
    parentData?: any
    isInPast?: boolean
}

export const ComponentFinder = ({
    data,
    componentKey,
    renderOptions,
    parentData,
    isInPast = false,
}: IComponentFinderProps) => {
    const { screenType } = useScreenTypeContext()
    const { past } = useBoundStore(
        useShallow((s) => {
            return {
                past: s.past,
            }
        })
    )
    const { sectionData } = useSectionV2Context()

    const pastToUse = past ? past : isInPast

    if (!renderOptions?.visible || renderOptions?.visible.includes(screenType))
        if (
            !renderOptions?.hidden ||
            !renderOptions?.hidden.includes(screenType)
        )
            switch (componentKey) {
                case 'text':
                    return (
                        <Text
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                            past={pastToUse}
                        />
                    )
                case 'telephoneNumber':
                    return (
                        <Text
                            data={data}
                            renderOptions={renderOptions}
                            past={pastToUse}
                        />
                    )
                case 'fromToTimes':
                    return (
                        <FromToTimes
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'spaceBetween':
                    return (
                        <SpaceBetween
                            data={data}
                            renderOptions={renderOptions}
                            past={pastToUse}
                        />
                    )
                case 'usersList':
                    return (
                        <UsersList
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                            past={pastToUse}
                        />
                    )
                case 'fileList':
                    return (
                        <FileList
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'iterator':
                    return (
                        <Iterator
                            data={data}
                            renderOptions={renderOptions}
                            past={pastToUse}
                        />
                    )
                case 'simpleFromToTimes':
                    return (
                        <SimpleFromToTimes
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'expandableCard':
                    return (
                        <ExpandableCard
                            data={data}
                            renderOptions={renderOptions}
                            parentData={parentData}
                            past={pastToUse}
                        />
                    )
                case 'simpleCard':
                    return (
                        <SimpleCard
                            data={data}
                            renderOptions={renderOptions}
                            parentData={parentData}
                            past={pastToUse}
                        />
                    )
                case 'timeDuration':
                    return (
                        <TimeDuration
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )

                case 'spacer':
                    return <Spacer data={data} renderOptions={renderOptions} />
                case 'icon':
                    return renderOptions?.ticket ? (
                        <TicketIcon
                            name={renderOptions.icon as TEventrIcons}
                            iconColour={
                                pastToUse
                                    ? ColoursObj.shades.shade2
                                    : renderOptions.colour
                                    ? recursiveFindValue(
                                          renderOptions.colour,
                                          ColoursObj
                                      ) ?? ColoursObj.core.primary
                                    : ColoursObj.core.primary
                            }
                        />
                    ) : (
                        <Icon
                            width={renderOptions.width}
                            height={renderOptions.height}
                            name={renderOptions.icon as TEventrIcons}
                            iconColour={
                                pastToUse
                                    ? renderOptions.bubble
                                        ? ColoursObj.backgrounds.secondary
                                        : ColoursObj.shades.shade2
                                    : renderOptions.colour
                                    ? recursiveFindValue(
                                          renderOptions.colour,
                                          ColoursObj
                                      ) ?? ColoursObj.core.primary
                                    : ColoursObj.core.primary
                            }
                            bubble={renderOptions.bubble}
                            bubbleColour={
                                pastToUse
                                    ? ColoursObj.shades.shade2
                                    : recursiveFindValue(
                                          renderOptions.bubbleColour ??
                                              'core.primary',
                                          ColoursObj
                                      )
                            }
                        />
                    )
                case 'container':
                    return (
                        <Container
                            data={data}
                            renderOptions={renderOptions}
                            parentData={parentData}
                            past={pastToUse}
                        />
                    )
                case 'arrayFinder':
                    return (
                        <ArrayFinder
                            data={data}
                            renderOptions={renderOptions}
                            past={pastToUse}
                        />
                    )
                case 'shouldRender':
                    return (
                        <ShouldRender
                            data={data}
                            renderOptions={renderOptions}
                            parentData={parentData}
                            past={pastToUse}
                        />
                    )
                case 'editButton':
                    return (
                        <EditButton
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )

                case 'editArrowIcon':
                    return (
                        <EditArrowIcon
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'counter':
                    return (
                        <Counter
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'subPageEditButton':
                    return (
                        <SubItemEditButton
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'pushPathContainer':
                    return (
                        <PushPathContainer
                            data={data}
                            renderOptions={renderOptions}
                            parentData={parentData}
                            past={pastToUse}
                        />
                    )
                case 'listHeader':
                    return (
                        <ListHeader
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'roomBody':
                    return (
                        <RoomBody
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )

                case 'flightPassengersBody':
                    return (
                        <FlightPassengersBody
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'transportPassengersBody':
                    return (
                        <TransportPassengersBody
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'parkingCarBody':
                    return (
                        <ParkingCarBody
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )
                case 'hireCarsPassengersBody':
                    return (
                        <HireCarsPassengersBody
                            id={`${sectionData?.sectionName.toLowerCase()}-`}
                            data={data}
                            renderOptions={renderOptions}
                        />
                    )

                case 'activitiesCard':
                    return (
                        <ActivityCard
                            id={`${sectionData?.sectionName.toLowerCase()}`}
                            data={data}
                            past={pastToUse}
                        />
                    )
                case 'flightCard':
                    return (
                        <FlightCard
                            id={`${sectionData?.sectionName.toLowerCase()}`}
                            data={data}
                            past={pastToUse}
                        />
                    )
                case 'hotelCard':
                    return (
                        <HotelCard
                            id={`${sectionData?.sectionName.toLowerCase()}`}
                            data={data}
                            past={pastToUse}
                        />
                    )
                case 'parkingCard':
                    return (
                        <ParkingCard
                            id={`${sectionData?.sectionName.toLowerCase()}`}
                            data={data}
                            past={pastToUse}
                        />
                    )
                case 'transportCard':
                    return (
                        <TransportCard
                            id={`${sectionData?.sectionName.toLowerCase()}`}
                            data={data}
                            past={pastToUse}
                        />
                    )
                case 'hireCarCard':
                    return (
                        <HireCarCard
                            id={`${sectionData?.sectionName.toLowerCase()}`}
                            data={data}
                            past={pastToUse}
                        />
                    )
                default:
                    return null
            }

    return null
}
